import { useSuspenseQuery } from "@tanstack/react-query"
import { trpc } from "app/trpc"
import type { GetMaintenanceResult } from "app/core/queries/getMaintenance"
import type { GetAllGradesResult } from "app/grades/queries/getAllGrades"
import { createContext, useContext } from "react"

type SystemInfoContextValue = {
  maintenance: GetMaintenanceResult
  grades: GetAllGradesResult
  token: string | undefined
}

const SystemInfoContext = createContext<SystemInfoContextValue | null>(null)

/**
 * SystemInfoProvider fetches system-level data (maintenance status, grades) once at the app level
 * to prevent duplicate queries from multiple components.
 */
export const SystemInfoProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { data: maintenance } = useSuspenseQuery({
    ...trpc.core.getMaintenance.queryOptions(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60 * 1000 * 60 * 60, // 1 hour
  })

  const { data: grades } = useSuspenseQuery({
    ...trpc.grades.getAllGrades.queryOptions(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60 * 1000 * 60 * 60 * 24, // 1 day
  })

  const { data: token } = useSuspenseQuery({
    ...trpc.core.getGTMToken.queryOptions(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60 * 1000 * 60 * 60 * 24, // 1 day
  })

  const value = {
    maintenance,
    grades,
    token,
  }

  return (
    <SystemInfoContext.Provider value={value}>
      {children}
    </SystemInfoContext.Provider>
  )
}

/**
 * Hook to get the maintenance status from context (loaded once at app level)
 */
export const useMaintenanceFromContext = () => {
  const context = useContext(SystemInfoContext)
  return context?.maintenance
}

/**
 * Hook to get all grades from context (loaded once at app level)
 */
export const useGradesFromContext = () => {
  const context = useContext(SystemInfoContext)
  return context?.grades ?? []
}
/**
 * Hook to get the GTM token from context (loaded once at app level)
 */
export const useGTMTokenFromContext = () => {
  const context = useContext(SystemInfoContext)
  return context?.token ?? undefined
}
